import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
  stack: string;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: '', stack: '' };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack ?? '' : '',
    };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Erro capturado pelo ErrorBoundary:', error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#F6F6F4] flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 p-8 text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-orange-50 text-[#E8610A] rounded-xl mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Ops, algo deu errado
            </h1>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Aconteceu um erro inesperado nesta tela. Tente recarregar — seus
              pedidos e dados continuam salvos com segurança.
            </p>
            <button
              onClick={this.handleReload}
              className="inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3 rounded-lg hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Recarregar página
            </button>
            {this.state.message && (
              <div className="mt-6 text-left border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                  Detalhe técnico (envie se pedir ajuda)
                </p>
                <p className="text-xs text-gray-500 break-words font-mono">
                  {this.state.message}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}